import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Package, MapPin, Clock, RefreshCw, CheckCircle, Truck,
  ShieldCheck, AlertTriangle, Navigation, Store, User,
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { deliveryAPI } from '../../services/api';
import { formatPrice } from '../../utils/helpers';
import DeliveryLayout from '../../components/delivery/DeliveryLayout';
import { toast } from '../../hooks/use-toast';

// Fix leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const storeIcon = new L.DivIcon({
  html: '<div style="background:#f97316;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,.4)"><svg width="16" height="16" fill="white" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/></svg></div>',
  iconSize: [32, 32], iconAnchor: [16, 32], className: '',
});
const buyerIcon = new L.DivIcon({
  html: '<div style="background:#22c55e;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,.4)"><svg width="16" height="16" fill="white" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></div>',
  iconSize: [32, 32], iconAnchor: [16, 32], className: '',
});

// Simulated coords (in production: geocode real addresses)
const simulateCoords = (seed) => {
  const hash = seed.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return { lat: 19.42 + (hash % 100) * 0.002 - 0.1, lng: -99.13 + (hash % 80) * 0.002 - 0.08 };
};

// ── PIN Input Component ──
const PinInput = ({ onComplete, error }) => {
  const [digits, setDigits] = useState(['', '', '', '']);
  const refs = [useRef(), useRef(), useRef(), useRef()];

  const handleChange = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...digits];
    next[idx] = val;
    setDigits(next);
    if (val && idx < 3) refs[idx + 1].current?.focus();
    if (next.every(d => d !== '')) onComplete(next.join(''));
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) refs[idx - 1].current?.focus();
  };

  return (
    <div>
      <div className="flex justify-center gap-3">
        {digits.map((d, i) => (
          <input key={i} ref={refs[i]} type="text" inputMode="numeric" maxLength={1}
            value={d} onChange={e => handleChange(i, e.target.value)} onKeyDown={e => handleKeyDown(i, e)}
            className={`w-16 h-20 text-center text-3xl font-black rounded-xl border-2 bg-gray-900 text-white outline-none transition-all
              ${error ? 'border-red-500 animate-shake' : d ? 'border-orange-500' : 'border-gray-600 focus:border-orange-400'}`}
            data-testid={`pin-digit-${i}`}
          />
        ))}
      </div>
      {error && (
        <div className="flex items-center justify-center gap-2 mt-3 text-red-400 text-sm animate-pulse">
          <AlertTriangle className="w-4 h-4" /> Codigo invalido
        </div>
      )}
    </div>
  );
};

const DeliveryOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pinError, setPinError] = useState(null); // order_id of error
  const [processing, setProcessing] = useState(null);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await deliveryAPI.getMyDeliveries();
      const active = data.filter(o => !['delivered_local', 'delivered_national', 'delivered'].includes(o.status) && o.delivery_status !== 'delivered');
      setOrders(active);
    } catch {
      toast({ title: 'Error cargando pedidos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const handlePickup = async (orderId, pin) => {
    setPinError(null);
    setProcessing(orderId);
    try {
      await deliveryAPI.confirmPickup(orderId, pin);
      toast({ title: 'Recogida confirmada!', description: 'En camino al cliente' });
      loadOrders();
    } catch (err) {
      if (err.response?.status === 400) setPinError(orderId);
      toast({ title: 'Error', description: err.response?.data?.detail || 'PIN incorrecto', variant: 'destructive' });
    } finally {
      setProcessing(null);
    }
  };

  const handleDeliver = async (orderId, pin) => {
    setPinError(null);
    setProcessing(orderId);
    try {
      const result = await deliveryAPI.confirmDelivery(orderId, pin);
      toast({ title: 'Entrega confirmada!', description: `Ganaste ${formatPrice(result.earned)}` });
      loadOrders();
    } catch (err) {
      if (err.response?.status === 400) setPinError(orderId);
      toast({ title: 'Error', description: err.response?.data?.detail || 'PIN incorrecto', variant: 'destructive' });
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return <DeliveryLayout><div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 text-orange-500 animate-spin" /></div></DeliveryLayout>;
  }

  return (
    <DeliveryLayout>
      <div className="space-y-6 max-w-2xl mx-auto">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Mis Pedidos</h1>
          <Button variant="ghost" onClick={loadOrders} className="text-gray-400 hover:text-white"><RefreshCw className="w-5 h-5" /></Button>
        </div>

        {orders.length === 0 ? (
          <Card className="bg-gray-800 border-gray-700 p-12 text-center">
            <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">No tienes pedidos activos</h2>
            <p className="text-gray-400">Conectate y acepta pedidos desde el dashboard</p>
          </Card>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => {
              const oid = order.order_id || order._id;
              const isAssigned = order.status === 'driver_assigned';
              const isInTransit = order.status === 'in_transit';
              const sellerCoords = simulateCoords(order.items?.[0]?.seller_id || 'seller');
              const buyerCoords = simulateCoords(order.user_id || 'buyer');
              const addr = order.shipping_address || {};

              return (
                <Card key={oid} className="bg-gray-800 border-gray-700 overflow-hidden" data-testid={`order-card-${oid}`}>
                  {/* Status bar */}
                  <div className={`px-4 py-2 flex items-center justify-between ${
                    isAssigned ? 'bg-cyan-600' : isInTransit ? 'bg-orange-500' : 'bg-gray-600'
                  }`}>
                    <span className="text-white font-medium text-sm flex items-center gap-2">
                      {isAssigned ? <><Store className="w-4 h-4" /> Yendo a la tienda</> :
                       isInTransit ? <><Truck className="w-4 h-4" /> En camino al cliente</> :
                       <><Package className="w-4 h-4" /> {order.status}</>}
                    </span>
                    <span className="text-white/80 text-sm font-mono">#{oid.slice(0, 6).toUpperCase()}</span>
                  </div>

                  {/* MAP */}
                  <div className="h-48" data-testid="order-map">
                    <MapContainer
                      center={[(sellerCoords.lat + buyerCoords.lat) / 2, (sellerCoords.lng + buyerCoords.lng) / 2]}
                      zoom={13} style={{ height: '100%', width: '100%' }}
                      zoomControl={false} attributionControl={false}
                    >
                      <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
                      <Marker position={[sellerCoords.lat, sellerCoords.lng]} icon={storeIcon}>
                        <Popup><span className="text-sm font-medium">Tienda</span></Popup>
                      </Marker>
                      <Marker position={[buyerCoords.lat, buyerCoords.lng]} icon={buyerIcon}>
                        <Popup><span className="text-sm font-medium">Cliente</span></Popup>
                      </Marker>
                      <Polyline positions={[[sellerCoords.lat, sellerCoords.lng], [buyerCoords.lat, buyerCoords.lng]]}
                        pathOptions={{ color: '#f97316', weight: 3, dashArray: '8 8', opacity: 0.7 }} />
                    </MapContainer>
                  </div>

                  <div className="p-4 space-y-4">
                    {/* Order info */}
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-white font-medium">{order.items?.length || 0} productos</p>
                        <p className="text-2xl font-bold text-orange-500">{formatPrice(order.total || 0)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-400 text-sm flex items-center gap-1 justify-end">
                          <Clock className="w-4 h-4" />
                          {new Date(order.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        {order.heisenberg_distance_km && (
                          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full mt-1 inline-block">
                            {order.heisenberg_distance_km} km
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Address */}
                    <div className="bg-gray-700/50 rounded-lg p-3">
                      <div className="flex items-start gap-3">
                        {isAssigned ? <Store className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" /> : <User className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />}
                        <div>
                          <p className="text-gray-400 text-xs mb-1">{isAssigned ? 'Recoger en:' : 'Entregar a:'}</p>
                          <p className="text-white">{isAssigned ? (order.items?.[0]?.seller_name || 'Tienda del vendedor') : (addr.name || order.buyer_name || 'Cliente')}</p>
                          <p className="text-gray-500 text-sm">{addr.address || ''} {addr.city || ''}</p>
                        </div>
                      </div>
                    </div>

                    {/* ═══ TRIPLE CHECK CUANTICO: PIN INPUT ═══ */}
                    <div className="bg-gray-900/70 rounded-xl p-5 border border-gray-700" data-testid="pin-section">
                      <div className="text-center mb-4">
                        <ShieldCheck className={`w-8 h-8 mx-auto mb-2 ${isAssigned ? 'text-cyan-400' : 'text-orange-400'}`} />
                        <p className="text-white font-semibold text-sm">
                          {isAssigned ? 'Codigo de Recogida' : 'Codigo de Entrega'}
                        </p>
                        <p className="text-gray-500 text-xs mt-1">
                          {isAssigned ? 'Pide el codigo al vendedor' : 'Pide el codigo al cliente'}
                        </p>
                      </div>

                      <PinInput
                        error={pinError === oid}
                        onComplete={(pin) => {
                          if (isAssigned) handlePickup(oid, pin);
                          else if (isInTransit) handleDeliver(oid, pin);
                        }}
                      />

                      <div className="mt-4 text-center">
                        <Button disabled={processing === oid}
                          className={`px-8 py-3 font-semibold ${isAssigned ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-orange-500 hover:bg-orange-600'}`}
                          onClick={() => toast({ title: 'Ingresa el PIN de 4 digitos arriba' })}
                          data-testid={isAssigned ? 'confirm-pickup-btn' : 'confirm-delivery-btn'}
                        >
                          {processing === oid ? <RefreshCw className="w-5 h-5 animate-spin" /> :
                            isAssigned ? <><CheckCircle className="w-5 h-5 mr-2" /> Confirmar Recogida</> :
                            <><Truck className="w-5 h-5 mr-2" /> Confirmar Entrega</>}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DeliveryLayout>
  );
};

export default DeliveryOrders;
